import {
  ApiSearchResponseModel,
  AtmModel,
  BindAtmToShopResponse,
  ShopModel,
} from '../models';
import http from '../utils/http';
import { getBackendAuthTokens } from '../api/auth-api';

interface SearchParams {
  page?: number;
  limit?: number;
  username?: string;
}

interface SearchShopsParams extends SearchParams {
  includeAtmCount?: boolean;
}

interface SearchAtmsParams extends SearchParams {
  bindStatus?: 'all' | 'bound' | 'unassigned';
  idShop?: number;
}

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const DROPDOWN_PAGE_LIMIT = 500;

function getAuthenticatedBackends(): BackendAuthToken[] {
  return getBackendAuthTokens();
}

function getBackend(backendName: string): BackendAuthToken {
  const backend = getAuthenticatedBackends().find(
    (item) => item.name === backendName,
  );

  if (!backend) {
    throw new Error(
      `Backend "${backendName}" is not authenticated`,
    );
  }

  return backend;
}

function getBackendUrl(
  backend: BackendAuthToken,
  path: string,
): string {
  return `${backend.url.replace(/\/$/, '')}${path}`;
}

function getBackendConfig(
  backend: BackendAuthToken,
  config: Record<string, any> = {},
): Record<string, any> {
  return {
    ...config,
    backend: backend.name,
  };
}

export async function serviceGetShops(
  params: SearchShopsParams,
): Promise<ApiSearchResponseModel<ShopModel[]>> {
  const backends = getAuthenticatedBackends();

  if (!backends.length) {
    return {
      data: [],
      rowcount: 0,
      status: 'success',
    } as ApiSearchResponseModel<ShopModel[]>;
  }

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response =
          (await http.get(
            getBackendUrl(
              backend,
              '/api/business/shops',
            ),
            getBackendConfig(backend, {
              params,
            }),
          )) as ApiSearchResponseModel<ShopModel[]>;

        const shops = (response.data || []).map(
          (shop: ShopModel) => ({
            ...shop,
            backendName: backend.name,
            backendUrl: backend.url,
          }),
        );

        return {
          data: shops,
          rowcount: Number(response.rowcount) || 0,
        };
      } catch (error) {
        console.error(
          `[SHOPS] ${backend.name} request failed`,
          error,
        );

        return {
          data: [],
          rowcount: 0,
        };
      }
    }),
  );

  return {
    data: responses.flatMap(
      (response) => response.data,
    ),
    rowcount: responses.reduce(
      (total, response) =>
        total + response.rowcount,
      0,
    ),
    status: 'success',
  } as ApiSearchResponseModel<ShopModel[]>;
}

export async function serviceGetAtms(
  params: SearchAtmsParams,
): Promise<ApiSearchResponseModel<AtmModel[]>> {
  const backends = getAuthenticatedBackends();

  if (!backends.length) {
    return {
      data: [],
      rowcount: 0,
      status: 'success',
    } as ApiSearchResponseModel<AtmModel[]>;
  }

  const responses = await Promise.all(
    backends.map(async (backend) => {
      try {
        const response =
          (await http.get(
            getBackendUrl(
              backend,
              '/api/business/atms',
            ),
            getBackendConfig(backend, {
              params,
            }),
          )) as ApiSearchResponseModel<AtmModel[]>;

        const atms = (response.data || []).map(
          (atm: AtmModel) => ({
            ...atm,
            backendName: backend.name,
            backendUrl: backend.url,
          }),
        );

        return {
          data: atms,
          rowcount: Number(response.rowcount) || 0,
        };
      } catch (error) {
        console.error(
          `[ATMS] ${backend.name} request failed`,
          error,
        );

        return {
          data: [],
          rowcount: 0,
        };
      }
    }),
  );

  return {
    data: responses.flatMap(
      (response) => response.data,
    ),
    rowcount: responses.reduce(
      (total, response) =>
        total + response.rowcount,
      0,
    ),
    status: 'success',
  } as ApiSearchResponseModel<AtmModel[]>;
}

export async function serviceGetAllShops(
  username?: string,
): Promise<ShopModel[]> {
  const backends = getAuthenticatedBackends();

  if (!backends.length) {
    return [];
  }

  const results = await Promise.all(
    backends.map(async (backend) => {
      try {
        const firstPage =
          await serviceGetShopsForBackend(
            backend,
            {
              page: 0,
              limit: DROPDOWN_PAGE_LIMIT,
              username,
            },
          );

        const pageCount = Math.ceil(
          firstPage.rowcount /
            DROPDOWN_PAGE_LIMIT,
        );

        const remainingPages =
          await Promise.all(
            Array.from(
              {
                length: Math.max(
                  pageCount - 1,
                  0,
                ),
              },
              (_, index) =>
                serviceGetShopsForBackend(
                  backend,
                  {
                    page: index + 1,
                    limit: DROPDOWN_PAGE_LIMIT,
                    username,
                  },
                ),
            ),
          );

        return [
          firstPage,
          ...remainingPages,
        ].flatMap(
          (response) => response.data || [],
        );
      } catch (error) {
        console.error(
          `[SHOPS] ${backend.name} failed`,
          error,
        );

        return [];
      }
    }),
  );

  return results.flat();
}

async function serviceGetShopsForBackend(
  backend: BackendAuthToken,
  params: SearchShopsParams,
): Promise<ApiSearchResponseModel<ShopModel[]>> {
  const response =
    (await http.get(
      getBackendUrl(
        backend,
        '/api/business/shops',
      ),
      getBackendConfig(backend, {
        params,
      }),
    )) as ApiSearchResponseModel<ShopModel[]>;

  return {
    ...response,
    data: (response.data || []).map(
      (shop: ShopModel) => ({
        ...shop,
        backendName: backend.name,
        backendUrl: backend.url,
      }),
    ),
  };
}

export async function serviceGetAllAtms(
  username?: string,
): Promise<AtmModel[]> {
  const backends = getAuthenticatedBackends();

  if (!backends.length) {
    return [];
  }

  const results = await Promise.all(
    backends.map(async (backend) => {
      try {
        const firstPage =
          await serviceGetAtmsForBackend(
            backend,
            {
              page: 0,
              limit: DROPDOWN_PAGE_LIMIT,
              username,
              bindStatus: 'all',
            },
          );

        const pageCount = Math.ceil(
          firstPage.rowcount /
            DROPDOWN_PAGE_LIMIT,
        );

        const remainingPages =
          await Promise.all(
            Array.from(
              {
                length: Math.max(
                  pageCount - 1,
                  0,
                ),
              },
              (_, index) =>
                serviceGetAtmsForBackend(
                  backend,
                  {
                    page: index + 1,
                    limit: DROPDOWN_PAGE_LIMIT,
                    username,
                    bindStatus: 'all',
                  },
                ),
            ),
          );

        return [
          firstPage,
          ...remainingPages,
        ].flatMap(
          (response) => response.data || [],
        );
      } catch (error) {
        console.error(
          `[ATMS] ${backend.name} failed`,
          error,
        );

        return [];
      }
    }),
  );

  return results.flat();
}

async function serviceGetAtmsForBackend(
  backend: BackendAuthToken,
  params: SearchAtmsParams,
): Promise<ApiSearchResponseModel<AtmModel[]>> {
  const response =
    (await http.get(
      getBackendUrl(
        backend,
        '/api/business/atms',
      ),
      getBackendConfig(backend, {
        params,
      }),
    )) as ApiSearchResponseModel<AtmModel[]>;

  return {
    ...response,
    data: (response.data || []).map(
      (atm: AtmModel) => ({
        ...atm,
        backendName: backend.name,
        backendUrl: backend.url,
      }),
    ),
  };
}

export async function serviceBindAtmToShop(
  idAtm: number,
  idShop: number,
  backendName: string,
): Promise<BindAtmToShopResponse> {
  const backend = getBackend(backendName);

  const response =
    await http.post(
      getBackendUrl(
        backend,
        '/api/business/atm/bind-to-shop',
      ),
      {
        idAtm,
        idShop,
      },
      getBackendConfig(backend),
    );

  return response.data;
}